/**
 * Cloudflare Workers auth helper.
 * Talks to CF Workers /api/auth/* endpoints and stores JWT in sessionStorage.
 */

const CF_API = () => (import.meta as any).env.VITE_APP_CF_API_URL || ''

const TOKEN_KEY = 'cc_cf_token'
const USER_KEY = 'cc_cf_user'

export function getStoredToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY)
}

export function getStoredUser(): { userId: string; username: string } | null {
  const raw = sessionStorage.getItem(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function storeSession(token: string, user: { userId: string; username: string }) {
  sessionStorage.setItem(TOKEN_KEY, token)
  sessionStorage.setItem(USER_KEY, JSON.stringify(user))
}

export function clearSession() {
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(USER_KEY)
}

async function cfPost(path: string, body: Record<string, unknown>) {
  const res = await fetch(`${CF_API()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) {
    throw Object.assign(new Error(data.error || res.statusText), {
      name: data.code || 'AuthError',
    })
  }
  return data
}

export async function cfSignIn(email: string, password: string) {
  const data = await cfPost('/api/auth/signin', { email, password })
  storeSession(data.token, { userId: data.user.id, username: data.user.email })
  return data
}

export async function cfSignUp(email: string, password: string) {
  return cfPost('/api/auth/signup', { email, password })
}

export async function cfConfirmSignUp(email: string, code: string) {
  return cfPost('/api/auth/confirm', { email, code })
}

export async function cfResendCode(email: string) {
  return cfPost('/api/auth/resend-code', { email })
}

export async function cfResetPassword(email: string) {
  return cfPost('/api/auth/forgot-password', { email })
}

export async function cfConfirmResetPassword(email: string, code: string, newPassword: string) {
  return cfPost('/api/auth/forgot-password-submit', { email, code, newPassword })
}

export async function cfChangePassword(oldPassword: string, newPassword: string) {
  const token = getStoredToken()
  if (!token) throw new Error('Not signed in')
  return cfPost('/api/auth/change-password', { oldPassword, newPassword })
}

export async function cfGetCurrentUser() {
  const user = getStoredUser()
  if (!user) throw new Error('Not signed in')
  return user
}

export async function cfGetAuthHeaders(): Promise<Record<string, string>> {
  const token = getStoredToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

export async function cfSignOut() {
  clearSession()
}

export async function cfDeleteUser() {
  const token = getStoredToken()
  if (!token) throw new Error('Not signed in')
  const res = await fetch(`${CF_API()}/api/user`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
  if (!res.ok) throw new Error('Failed to delete user')
  clearSession()
}
